import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy } from 'react';
import { RootLayout } from './RootLayout';
import { ProtectedRoute, PublicRoute } from './RouteGuards';

// Eager load critical pages
import Landing from '@/pages/Landing';
import Auth from '@/pages/Auth';
import NotFound from '@/pages/NotFound';
import About from '@/pages/About';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';

// Lazy load protected/authenticated pages
const Assessments = lazy(() => import('@/pages/Assessments'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const UserProfile = lazy(() => import('@/pages/UserProfile'));
const TakeAssessment = lazy(() => import('@/pages/TakeAssessment'));
const AdminAssessments = lazy(() => import('@/pages/AdminAssessments'));
const ManageUsers = lazy(() => import('@/pages/ManageUsers'));
const Contact = lazy(() => import('@/pages/Contact'));
const Help = lazy(() => import('@/pages/Help'));
const Upgrade = lazy(() => import('@/pages/Upgrade'));
const UserFeedback = lazy(() => import('@/pages/UserFeedback'));
const ManageCountryPricing = lazy(() => import('@/pages/ManageCountryPricing'));
const PaymentSuccess = lazy(() => import('@/pages/PaymentSuccess'));
const Results = lazy(() => import('@/pages/Results'));
const Blogs = lazy(() => import('@/pages/Blogs'));
const AdminMonitoring = lazy(() => import('@/pages/AdminMonitoring'));

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <NotFound />,
    children: [
      {
        index: true,
        element: <Landing />,
      },
      {
        path: 'assessments',
        element: (
          <ProtectedRoute>
            <Assessments />
          </ProtectedRoute>
        ),
      },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <ProtectedRoute>
            <UserProfile />
          </ProtectedRoute>
        ),
      },
      {
        path: 'assessment/:id',
        element: (
          <ProtectedRoute>
            <TakeAssessment />
          </ProtectedRoute>
        ),
      },
      {
        path: 'manage-assessments',
        element: (
          <ProtectedRoute>
            <AdminAssessments />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/users',
        element: (
          <ProtectedRoute>
            <ManageUsers />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/country-pricing',
        element: (
          <ProtectedRoute>
            <ManageCountryPricing />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/monitoring',
        element: (
          <ProtectedRoute>
            <AdminMonitoring />
          </ProtectedRoute>
        ),
      },
      {
        path: 'user-feedback',
        element: (
          <ProtectedRoute>
            <UserFeedback />
          </ProtectedRoute>
        ),
      },
      {
        path: 'about',
        element: <About />,
      },
      {
        path: 'privacy-policy',
        element: <PrivacyPolicy />,
      },
      {
        path: 'terms-of-service',
        element: <TermsOfService />,
      },
      {
        path: 'contact',
        element: (
          <ProtectedRoute>
            <Contact />
          </ProtectedRoute>
        ),
      },
      {
        path: 'help',
        element: (
          <ProtectedRoute>
            <Help />
          </ProtectedRoute>
        ),
      },
      {
        path: 'upgrade',
        element: (
          <ProtectedRoute>
            <Upgrade />
          </ProtectedRoute>
        ),
      },
      {
        path: 'results',
        element: (
          <ProtectedRoute>
            <Results />
          </ProtectedRoute>
        ),
      },
      {
        path: 'blogs',
        element: <Blogs />,
      },
      {
        path: 'payment-success',
        element: (
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        ),
      },
      {
        path: 'auth',
        element: (
          <PublicRoute>
            <Auth />
          </PublicRoute>
        ),
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]);
